import { formatCurrency } from '@/components/molecules/financialImpact';
import type { PortalMembership } from '@/hooks/portal/usePortalMemberships';
import { formatDateOnly } from '@/lib/dates/date-only';

type BadgeVariant = 'success' | 'warning' | 'secondary';

const STATUS_BADGE: Record<string, { label: string; variant: BadgeVariant }> = {
  ACTIVE: { label: 'Active', variant: 'success' },
  PENDING: { label: 'Pending', variant: 'warning' },
  PAST_DUE: { label: 'Past due', variant: 'warning' },
  SUSPENDED: { label: 'Suspended', variant: 'warning' },
  CANCELLED: { label: 'Cancelled', variant: 'secondary' },
  EXPIRED: { label: 'Expired', variant: 'secondary' },
};

export function membershipStatusBadge(status: string): {
  label: string;
  variant: BadgeVariant;
} {
  return STATUS_BADGE[status] ?? { label: status, variant: 'secondary' };
}

/** Human interval unit — "month" / "3 months" / "year". */
function intervalLabel(value: number, unit: string): string {
  const singular = unit.toLowerCase().replace(/s$/, '');
  return value === 1 ? singular : `${value} ${singular}s`;
}

/** "$29.99 / month" — the recurring billing summary for a membership card. */
export function formatBilling(m: PortalMembership): string {
  // Given free with nothing left to bill: say when it ends, not "$0.00 / month".
  if (m.isComplimentary && !(m.billingAmount > 0)) {
    return m.contractEndDate ? `Free until ${formatDateOnly(m.contractEndDate)}` : 'Free';
  }
  return `${formatCurrency(m.billingAmount)} / ${intervalLabel(
    m.billingIntervalValue,
    m.billingIntervalUnit,
  )}`;
}

/**
 * " · renews Oct 3, 2027". A membership given on a free option ends instead of
 * renewing: " · ends …" when extra units still bill, nothing when the billing
 * summary already said "Free until …".
 */
export function formatRenews(m: {
  isComplimentary?: boolean;
  billingAmount?: number;
  nextBillingDate: string | null;
  contractEndDate?: string | null;
}): string {
  if (m.isComplimentary) {
    return (m.billingAmount ?? 0) > 0 && m.contractEndDate
      ? ` · ends ${formatDateOnly(m.contractEndDate)}`
      : '';
  }
  return m.nextBillingDate ? ` · renews ${formatDateOnly(m.nextBillingDate)}` : '';
}
